/**
 * Smart push permission flow.
 * Permission is NEVER requested on app boot. It is gated behind meaningful
 * engagement signals (match join, prediction submit, nation follow, creator
 * interaction, hype engagement). The premium prompt is shown once the
 * "ready" threshold is met.
 */
import { useSyncExternalStore } from "react";
import { trackEvent } from "@/lib/audit";
import { registerSubscription, unregisterSubscription } from "./pushSubscription";
import { ensurePushServiceWorker } from "./serviceWorker";

const SIGNAL_KEY = "vs:push:signals:v1";
const PROMPT_KEY = "vs:push:prompt:v1";
const READY_THRESHOLD = 2;

export type EngagementSignal =
  | "match_join"
  | "prediction_submit"
  | "nation_follow"
  | "creator_interact"
  | "hype_burst"
  | "quiniela_join";

interface Signals {
  count: number;
  last: number;
  history: { kind: EngagementSignal; at: number }[];
}

interface PromptState {
  shown: boolean;
  shownAt?: number;
  dismissed?: boolean;
}

function readSignals(): Signals {
  if (typeof window === "undefined") return { count: 0, last: 0, history: [] };
  try { return JSON.parse(window.localStorage.getItem(SIGNAL_KEY) ?? "") as Signals; }
  catch { return { count: 0, last: 0, history: [] }; }
}

function writeSignals(s: Signals): void {
  if (typeof window === "undefined") return;
  s.history = s.history.slice(-20);
  window.localStorage.setItem(SIGNAL_KEY, JSON.stringify(s));
}

function readPrompt(): PromptState {
  if (typeof window === "undefined") return { shown: false };
  try { return JSON.parse(window.localStorage.getItem(PROMPT_KEY) ?? "") as PromptState; }
  catch { return { shown: false }; }
}

function writePrompt(p: PromptState): void {
  if (typeof window !== "undefined") {
    window.localStorage.setItem(PROMPT_KEY, JSON.stringify(p));
  }
  notifySubscribers();
}

const subscribers = new Set<() => void>();
function notifySubscribers() {
  cachedSnapshot = null;
  subscribers.forEach((fn) => fn());
}

// Stable snapshot cache — useSyncExternalStore requires referential
// equality across renders when nothing changed, otherwise React enters
// an infinite re-render loop ("Maximum update depth exceeded").
let cachedSnapshot: PromptStatus | null = null;
const SERVER_SNAPSHOT: PromptStatus = {
  ready: false,
  permission: "unsupported",
  shown: false,
  dismissed: false,
  signals: 0,
};

export function getPermission(): NotificationPermission | "unsupported" {
  if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
  return Notification.permission;
}

export function isPushSupported(): boolean {
  return typeof window !== "undefined" && "Notification" in window;
}

export function recordEngagement(kind: EngagementSignal): void {
  const s = readSignals();
  s.count += 1;
  s.last = Date.now();
  s.history.push({ kind, at: Date.now() });
  writeSignals(s);
  notifySubscribers();
  void trackEvent("push_engagement_signal", { kind, count: s.count });
}

export interface PromptStatus {
  ready: boolean;
  permission: NotificationPermission | "unsupported";
  shown: boolean;
  dismissed: boolean;
  signals: number;
}

export function pushPromptStatus(): PromptStatus {
  if (typeof window === "undefined") return SERVER_SNAPSHOT;
  if (cachedSnapshot) return cachedSnapshot;
  const s = readSignals();
  const p = readPrompt();
  const perm = getPermission();
  cachedSnapshot = {
    ready:
      perm !== "unsupported" &&
      perm === "default" &&
      !p.shown &&
      !p.dismissed &&
      s.count >= READY_THRESHOLD,
    permission: perm,
    shown: !!p.shown,
    dismissed: !!p.dismissed,
    signals: s.count,
  };
  return cachedSnapshot;
}

export function usePushPromptStatus(): PromptStatus {
  return useSyncExternalStore(
    (cb) => {
      subscribers.add(cb);
      return () => subscribers.delete(cb);
    },
    pushPromptStatus,
    () => SERVER_SNAPSHOT,
  );
}

export async function activatePush(): Promise<NotificationPermission | "unsupported"> {
  const perm = getPermission();
  if (perm === "unsupported") return "unsupported";
  writePrompt({ shown: true, shownAt: Date.now() });
  void trackEvent("push_permission_requested", {});
  try {
    const result = await Notification.requestPermission();
    void trackEvent("push_permission_result", { result });
    if (result === "granted") {
      registerSubscription();
      // PR-38B-C — register SW so notifications survive page lifecycle.
      void ensurePushServiceWorker();
    } else if (result === "denied") {
      unregisterSubscription("denied");
    }
    notifySubscribers();
    return result;
  } catch {
    return "default";
  }
}

export function dismissPushPrompt(): void {
  writePrompt({ shown: true, dismissed: true, shownAt: Date.now() });
  void trackEvent("push_permission_dismissed", {});
}

export function resetPushPrompt(): void {
  if (typeof window !== "undefined") {
    window.localStorage.removeItem(PROMPT_KEY);
    window.localStorage.removeItem(SIGNAL_KEY);
  }
  notifySubscribers();
}